import React, { ReactNode, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Modal,
  notification,
  Popconfirm,
  Radio,
  Spin,
  Table,
  Tag,
} from 'antd';
import { ColumnProps } from 'antd/lib/table';

import DeviceSyncSource, {
  DeviceKeyList,
} from '@totem/components/administration/deviceSyncMerges/DeviceSyncSource';
import {
  ConflictDevice,
  MergeResult,
  SyncConflict,
  SyncConflictDetail,
} from '@totem/components/administration/deviceSyncMerges/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DateStringToLocal } from '@totem/utilities/dateUtilities';
import { DEVICE_SYNC_CONFLICTS_ENDPOINT } from '@totem/utilities/endpoints';

const styles = {
  spinner: { display: 'flex', justifyContent: 'center', padding: '2rem' },
  alert: { marginBottom: '1rem' },
};

const referenceLabels: Record<string, string> = {
  events: 'Events',
  eventsCleared: 'Cleared Events',
  ticketing_tickets: 'Tickets',
  deviceBackups: 'Backups',
  deviceVulnerabilities: 'Vulnerabilities',
  maintenanceWindows: 'Maintenance Windows',
  salesOrderLines: 'Sales Order Lines',
  deviceVolumes: 'Volumes',
};

type ComparisonRow = {
  key: string;
  label: string;
  render: (item: ConflictDevice) => ReactNode;
};

const comparisonRows: ComparisonRow[] = [
  { key: 'type', label: 'Type', render: (item) => item.device.type },
  { key: 'vendor', label: 'Vendor', render: (item) => item.device.vendor },
  {
    key: 'os',
    label: 'Operating System',
    render: (item) => item.device.operatingSystem,
  },
  { key: 'building', label: 'Building', render: (item) => item.buildingName },
  {
    key: 'controlSystem',
    label: 'Control System',
    render: (item) => item.controlSystemName,
  },
  {
    key: 'createdAt',
    label: 'Created',
    render: (item) => DateStringToLocal(item.device.createdAt),
  },
  {
    key: 'keys',
    label: 'Keys',
    render: (item) =>
      (item.device.keys ?? []).map((key) => (
        <div key={`${key.type}:${key.value}`}>
          {key.type}: {key.value}
        </div>
      )),
  },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    render: (item) => (item.device.ipAddresses ?? []).join(', '),
  },
  {
    key: 'software',
    label: 'Software Packages',
    render: (item) => (item.device.software ?? []).length,
  },
  ...Object.keys(referenceLabels).map((collection) => ({
    key: collection,
    label: referenceLabels[collection],
    render: (item: ConflictDevice) => item.references?.[collection] ?? 0,
  })),
];

type Props = {
  conflict: SyncConflict;
  onClose: (updated: boolean) => void;
};

const DeviceSyncMergeModal = ({ conflict, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [detail, setDetail] = useState<SyncConflictDetail | null>(null);
  const [survivorId, setSurvivorId] = useState<string>('');

  useEffect(() => {
    fetch(`${DEVICE_SYNC_CONFLICTS_ENDPOINT}/${conflict.id}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: SyncConflictDetail) => setDetail(result))
      .finally(() => setIsFetching(false));
  }, [conflict.id]);

  const isDeleted = (deviceId: string) =>
    conflict.devices.find((dev) => dev.id === deviceId)?.isDeleted ?? false;

  const matchedByRow: ComparisonRow = {
    key: 'matchedBy',
    label: 'Matched By',
    render: (item) => (
      <DeviceKeyList
        keys={
          conflict.devices.find((dev) => dev.id === item.device.id)
            ?.matchedKeys ?? null
        }
      />
    ),
  };

  const survivorName =
    detail?.devices.find((item) => item.device.id === survivorId)?.device
      .displayName ?? '';

  const handleMerge = () => {
    setIsMerging(true);

    fetch(`${DEVICE_SYNC_CONFLICTS_ENDPOINT}/${conflict.id}/merge`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ survivorId }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `Merge failed (${res.status})`);
        }
        return res.json();
      })
      .then((result: MergeResult) => {
        notification.success({
          message: 'Devices merged',
          description: `${result.mergedIds.length} device(s) merged into ${survivorName}.`,
        });

        onClose(true);
      })
      .catch((err: Error) => {
        notification.error({
          message: 'Unable to merge the devices',
          description: err.message,
        });
      })
      .finally(() => setIsMerging(false));
  };

  const columns: ColumnProps<ComparisonRow>[] = [
    {
      title: '',
      key: 'label',
      render: (_, row: ComparisonRow) => <b>{row.label}</b>,
      width: '160px',
    },
    ...(detail?.devices ?? []).map((item) => ({
      title: (
        <Radio
          checked={survivorId === item.device.id}
          disabled={isDeleted(item.device.id)}
          onChange={() => setSurvivorId(item.device.id)}
        >
          {item.device.displayName || item.device.id}
          {isDeleted(item.device.id) && (
            <Tag style={{ marginLeft: 8 }}>Deleted</Tag>
          )}
        </Radio>
      ),
      key: item.device.id,
      render: (_: unknown, row: ComparisonRow) => row.render(item),
    })),
  ];

  return (
    <Modal
      open
      title={<ModalTitle>Merge Devices</ModalTitle>}
      onCancel={() => onClose(false)}
      footer={
        <ModalFooter>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Popconfirm
            title="Merge these devices?"
            description={`The other devices are merged into ${survivorName} and deleted. This cannot be undone from the portal.`}
            okText="Merge"
            onConfirm={handleMerge}
            disabled={survivorId === ''}
          >
            <Button
              type="primary"
              danger
              disabled={survivorId === ''}
              loading={isMerging}
            >
              Merge
            </Button>
          </Popconfirm>
        </ModalFooter>
      }
      width={1000}
    >
      <Descriptions size="small" column={1} style={styles.alert}>
        <Descriptions.Item label="Tool record">
          <div>
            <DeviceSyncSource conflict={conflict} />
          </div>
        </Descriptions.Item>
      </Descriptions>
      <Alert
        style={styles.alert}
        type="info"
        showIcon
        message="Choose the device to keep."
        description="Keys, IP addresses, attributes, software, contract and sales order lines are combined onto it, and events, tickets, backups, vulnerabilities, maintenance windows and sales order lines are moved to it. Its own values win where both devices have one. The other devices are deleted and their keys removed."
      />
      {isFetching ? (
        <div style={styles.spinner}>
          <Spin />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={[matchedByRow, ...comparisonRows]}
          rowKey={(row) => row.key}
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
      )}
    </Modal>
  );
};

export default DeviceSyncMergeModal;
